import type { IJodit } from 'jodit/types/types';
import type { JoditToolbarConfig } from './jodit-toolbar.types';

/** Default JODIT editor configuration Sensible defaults for most use cases */
export const DEFAULT_JODIT_CONFIG: Partial<IJodit['options']> = {
  // Editor dimensions
  height: 'auto',
  width: 'auto',

  // Toolbar
  toolbar: true,
  toolbarSticky: false,
  toolbarAdaptive: false, // Show all buttons by default (not responsive)
  toolbarButtonSize: 'middle',

  // Editor behavior
  readonly: false,
  disabled: false,
  placeholder: '',
  enter: 'br', // Use <br> for email compatibility (not <p> with margins)
  defaultMode: 1, // WYSIWYG mode

  // HTML cleaning
  cleanHTML: {
    removeEmptyElements: false,
    fillEmptyParagraph: false, // Prevents trailing <p> at end of output
    replaceNBSP: false,
    allowTags: false, // false = disable allowlist = allow ALL tags (for email tables)
    denyTags: false, // false = deny nothing
  } as any,

  // Image handling - base64 inline (ADR-006 compliant)
  uploader: {
    insertImageAsBase64URI: true,
    url: '', // No CDN upload
  } as any,
  imageDefaultWidth: 600,

  // Enable iframe mode (isolates editor CSS from Angular host page)
  iframe: true,

  // Paste behavior - preserve HTML attributes during paste
  defaultActionOnPaste: 'insert_as_html',
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  processPasteHTML: false, // Don't modify pasted HTML

  // Status bar
  statusbar: true,
  showCharsCounter: true,
  showWordsCounter: true,
  showXPathInStatusbar: false,

  // Other settings
  spellcheck: true,
  language: 'en',
  direction: 'ltr',
  tabIndex: 0,

  // Iframe base styles (overridden per-document via injected <style> tag)
  iframeStyle:
    'body { margin: 8px; padding: 0; }' +
    'table { border-collapse: collapse; }' +
    'td, th { padding: 4px; vertical-align: top; }' +
    'img { max-width: 100%; }',
};

/** Email template editor configuration Optimized for creating HTML email templates with full document preservation */
export const EMAIL_JODIT_CONFIG: Partial<IJodit['options']> = {
  ...DEFAULT_JODIT_CONFIG,

  // Use <br> for email compatibility (email clients render <p> with unwanted margins)
  enter: 'br',

  // Enable iframe mode (isolates email CSS from host page)
  iframe: true,

  // Native full-document editing: Jodit handles DOCTYPE, <html>, <head>, <body> with all attributes
  editHTMLDocumentMode: true,

  // Sets the DOCTYPE declaration used in the editor's iframe
  iframeDoctype: '<!DOCTYPE html>',

  // HTML preservation for email — allow all tags/attributes so nothing is stripped
  cleanHTML: {
    removeEmptyElements: false,
    fillEmptyParagraph: false,
    replaceNBSP: false,
    replaceOldTags: false, // Keep <b>, <i> as-is
    allowTags: false, // false = no allowlist = allow ALL tags
    denyTags: false, // false = deny nothing
  } as any,

  // Disable automatic cleanup
  beautifyHTML: false,
};

/** Minimal toolbar configuration Basic text formatting only */
export const MINIMAL_TOOLBAR: JoditToolbarConfig = 'bold,italic,underline,|,ul,ol,|,link,|,undo,redo';

/** Standard toolbar configuration Common formatting and content insertion */
export const STANDARD_TOOLBAR: JoditToolbarConfig =
  'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,brush,|,align,|,image,link,table,|,undo,redo,|,source';

/**
 * Full toolbar configuration Shows all JODIT default buttons
 *
 * When set to `true`, JODIT displays all its built-in buttons. Combined with `toolbarAdaptive: false`, all buttons are
 * visible.
 *
 * JODIT's default buttons include:
 *
 * - Text formatting: bold, italic, underline, strikethrough, superscript, subscript
 * - Font styling: font, fontsize, brush, paragraph
 * - Lists & indentation: ul, ol, indent, outdent
 * - Alignment: align (dropdown)
 * - Insert: image, video, file, table, link, hr, symbols
 * - Structural: blockquote
 * - Edit: undo, redo, cut, copy, paste, selectall, eraser, copyformat
 * - View: source, fullsize, preview, print
 * - Info: about, dots
 */
export const FULL_TOOLBAR: JoditToolbarConfig = true;
